import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, X, Clock, Shield, ChevronDown, Book, Star, Trophy, Check, Lock, Zap } from 'lucide-react';
// --- Animation Wrappers ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 10
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-20px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const StaggerContainer = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.div initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={{
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const StaggerItem = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.div variants={{
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
}} className={className} data-id="element-2">
    {children}
  </motion.div>;
// --- Shared Components ---
const GoldButton = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <button onClick={onClick} className={`bg-gold-gradient hover:bg-gold-gradient-hover text-cherub-bg-main font-bold py-5 px-14 rounded-full transition-all duration-300 shadow-gold-glow hover:shadow-gold-glow-hover hover:-translate-y-1 ${className}`} data-id="element-3">
    {children}
  </button>;
const SecondaryButton = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <button onClick={onClick} className={`${`bg-transparent border-2 border-cherub-gold-main text-cherub-gold-main font-bold py-4 px-10 rounded-full transition-all duration-300 hover:bg-cherub-gold-soft hover:-translate-y-1 ${className}`} text-[18px]`} data-id="element-4">
    {children}
  </button>;
const GoldDivider = ({
  className = ''
}: {
  className?: string;
}) => <div className={`gold-divider w-full max-w-[250px] mx-auto ${className}`} data-id="element-5" />;
// --- Main App ---
export function App() {
  const [selectedPack, setSelectedPack] = useState<'pack1' | 'pack2'>('pack2');
  const [timeLeft, setTimeLeft] = useState({
    hours: 12,
    minutes: 45,
    seconds: 30
  });
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  // Meta Pixel Integration
  useEffect(() => {
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      const n: any = window.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      // @ts-ignore
      if (!window._fbq) window._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [] as any[];
      const scriptEl = document.createElement('script');
      scriptEl.async = true;
      scriptEl.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(scriptEl);
      // @ts-ignore
      fbq('init', '1300381271931772');
      // @ts-ignore
      fbq('track', 'PageView');
    } else {
      // @ts-ignore
      fbq('track', 'PageView');
    }
    // Add noscript fallback to body
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1300381271931772&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, []);
  // Simple countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cherub-bg-main text-cherub-text-secondary font-sans selection:bg-cherub-gold-soft selection:text-cherub-gold-bright" data-id="element-6">
      {/* SECTION 1: HERO */}
      <section className="relative h-[100dvh] md:h-auto md:min-h-[85vh] flex flex-col items-center py-6 md:py-12 px-6 bg-noise overflow-hidden" data-id="element-7">
        {/* Subtle radial gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(212,168,71,0.06)_0%,transparent_60%)] pointer-events-none" data-id="element-8" />

        <div className="relative-z w-full max-w-5xl mx-auto flex-1 flex flex-col items-center text-center justify-between" data-id="element-9">
          <div className="w-full flex flex-col items-center" data-id="element-10">
            <FadeIn data-id="element-11">
              <div className="mb-2 md:mb-4 flex flex-col items-center" data-id="element-12">
                <h2 className="font-serif md:text-xl text-cherub-text-main tracking-wide text-[26px]" data-id="element-13">
                  CHERUB AGENCY
                  <span className="text-cherub-gold-main text-xs md:text-sm align-top" data-id="element-14">
                    ™
                  </span>
                </h2>
                <GoldDivider className="mt-1 md:mt-2 w-20 md:w-24" data-id="element-15" />
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-16">
              <p className="text-cherub-gold-main font-bold md:text-sm tracking-[0.3em] uppercase mb-2 md:mb-3 text-[16px]" data-id="element-17">
                SISTEMA ZERO ANSIEDAD
              </p>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-18">
              <h1 className="font-serif font-bold lg:text-7xl text-cherub-text-main tracking-[-0.02em] mb-4 md:mb-6 max-w-4xl md:text-[32px] text-[28px] leading-tight" data-id="element-19">
                Todo lo que necesitas para salir de la ansiedad de una vez.
              </h1>
            </FadeIn>
          </div>

          <div className="w-full flex flex-col items-center" data-id="element-20">
            <FadeIn delay={0.25} className="w-full" data-id="element-21">
              <div className="mb-4 md:mb-6 max-w-2xl mx-auto" data-id="element-22">
                <img src="https://cdn.magicpatterns.com/uploads/52McLvuU4ZMvmSKNcCiws2/Diseno_sin_titulo_(1).png" alt="TDAH: El Sistema Definitivo" className="w-full h-auto drop-shadow-[0_0_50px_rgba(212,168,71,0.3)] rounded-lg max-h-[25vh] md:max-h-[40vh] object-contain" data-id="element-23" />
              </div>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-24">
              <p className="text-cherub-text-secondary max-w-2xl mb-4 md:mb-6 md:text-[18px] md:leading-relaxed text-[18px] px-2" data-id="element-25">
                El sistema N°1 para personas con ansiedad crónica en España —
                Del colapso a la libertad.
              </p>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} className="w-full flex flex-col items-center" data-id="element-26">
            <GoldDivider className="mb-4 md:mb-6" data-id="element-27" />

            <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-y-3 gap-x-6 md:gap-12 text-cherub-text-tertiary mb-6 md:mb-8" data-id="element-28">
              <div className="flex items-center gap-1.5 md:text-[14px] text-[13px] justify-center md:justify-start" data-id="element-29">
                <CheckCircle2 size={16} className="text-cherub-gold-main md:w-5 md:h-5" data-id="element-30" />{' '}
                100% PDF
              </div>
              <div className="flex items-center gap-1.5 md:text-[14px] text-[13px] justify-center md:justify-start" data-id="element-31">
                <CheckCircle2 size={16} className="text-cherub-gold-main md:w-5 md:h-5" data-id="element-32" />{' '}
                Acceso en 5 min
              </div>
              <div className="flex items-center gap-1.5 md:text-[14px] text-[13px] justify-center md:justify-start" data-id="element-33">
                <CheckCircle2 size={16} className="text-cherub-gold-main md:w-5 md:h-5" data-id="element-34" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center gap-1.5 md:text-[14px] text-[13px] justify-center md:justify-start" data-id="element-35">
                <CheckCircle2 size={16} className="text-cherub-gold-main md:w-5 md:h-5" data-id="element-36" />{' '}
                Pago seguro
              </div>
            </div>

            <SecondaryButton onClick={scrollToPricing} className="w-full md:w-auto text-lg md:text-xl py-5 md:py-6 px-8 md:px-16 mb-2" data-id="element-37">
              → VER LA OFERTA COMPLETA
            </SecondaryButton>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 1.5: STATS (Moved below the fold) */}
      <section className="py-10 px-6 bg-cherub-bg-main border-b border-cherub-gold-soft/20" data-id="element-38">
        <div className="max-w-4xl mx-auto" data-id="element-39">
          <FadeIn data-id="element-40">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16" data-id="element-41">
              <div className="text-center min-w-[100px]" data-id="element-42">
                <div className="text-4xl md:text-5xl font-bold text-cherub-gold-main mb-1" data-id="element-43">
                  +500
                </div>
                <div className="text-xs uppercase tracking-widest text-cherub-text-tertiary" data-id="element-44">
                  personas
                </div>
              </div>
              <div className="hidden md:block w-px h-12 bg-cherub-gold-soft/30" data-id="element-45" />
              <div className="text-center min-w-[100px]" data-id="element-46">
                <div className="text-4xl md:text-5xl font-bold text-cherub-gold-main mb-1" data-id="element-47">
                  6
                </div>
                <div className="text-xs uppercase tracking-widest text-cherub-text-tertiary" data-id="element-48">
                  Tomos
                </div>
              </div>
              <div className="hidden md:block w-px h-12 bg-cherub-gold-soft/30" data-id="element-49" />
              <div className="text-center min-w-[100px]" data-id="element-50">
                <div className="text-4xl md:text-5xl font-bold text-cherub-gold-main mb-1" data-id="element-51">
                  6
                </div>
                <div className="text-xs uppercase tracking-widest text-cherub-text-tertiary" data-id="element-52">
                  Bonos
                </div>
              </div>
              <div className="hidden md:block w-px h-12 bg-cherub-gold-soft/30" data-id="element-53" />
              <div className="text-center min-w-[100px]" data-id="element-54">
                <div className="text-4xl md:text-5xl font-bold text-cherub-gold-main mb-1" data-id="element-55">
                  4.9/5 <span className="text-3xl" data-id="element-56">⭐</span>
                </div>
                <div className="text-xs uppercase tracking-widest text-cherub-text-tertiary" data-id="element-57">
                  Valoración
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 2: TESTIMONIALS 1 */}
      <section className="py-8 px-6 bg-cherub-bg-secondary bg-noise" data-id="element-58">
        <div className="max-w-6xl mx-auto relative-z" data-id="element-59">
          <FadeIn className="text-center mb-6" data-id="element-60">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-61">
              TESTIMONIOS REALES
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-62">
              Personas que ya lo tienen
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-63">
            {[{
            name: 'Laura M.',
            city: 'Madrid',
            text: 'Llevaba 5 años con ataques de pánico casi diarios. Este sistema me dio las herramientas exactas que nadie más me había explicado. Hoy hace 3 meses que no tengo un ataque.'
          }, {
            name: 'Carlos R.',
            city: 'Barcelona',
            text: 'La claridad con la que explican la biología de la ansiedad es brutal. Entender qué me pasaba fue el primer paso. El protocolo del tomo 3 me salvó la vida literalmente.'
          }, {
            name: 'Elena S.',
            city: 'Valencia',
            text: 'Había gastado miles de euros en terapias que solo rascaban la superficie. Este megapack va directo a la raíz. Es duro, pero es la única verdad que me ha funcionado.'
          }].map((t, i) => <StaggerItem key={i} className="glass-card glass-card-hover rounded-2xl p-5 md:p-6 relative overflow-hidden" data-id="element-64">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-cherub-gold-main" data-id="element-65" />
                <p className="font-quote italic text-xl text-cherub-text-main mb-6 leading-relaxed" data-id="element-66">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-4" data-id="element-67">
                  <div className="w-12 h-12 rounded-full border border-cherub-gold-soft bg-cherub-bg-tertiary flex items-center justify-center text-cherub-gold-main font-serif font-bold text-lg" data-id="element-68">
                    {t.name[0]}
                  </div>
                  <div data-id="element-69">
                    <div className="font-semibold text-cherub-text-main" data-id="element-70">
                      {t.name}
                    </div>
                    <div className="text-sm text-cherub-gold-muted" data-id="element-71">
                      {t.city}
                    </div>
                  </div>
                </div>
              </StaggerItem>)}
          </StaggerContainer>
        </div>
      </section>

      {/* SECTION 3: PAIN / EMPATHY */}
      <section className="py-8 px-6 bg-cherub-bg-main bg-noise" data-id="element-72">
        <div className="max-w-4xl mx-auto relative-z" data-id="element-73">
          <FadeIn className="mb-6" data-id="element-74">
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main mb-3 leading-tight" data-id="element-75">
              ¿Cuántas noches más vas a terminar así?
            </h2>
            <p className="text-lg text-cherub-text-secondary leading-relaxed" data-id="element-76">
              Sabemos exactamente dónde estás. Porque hemos estado ahí. La
              ansiedad no es solo "estar nervioso", es una prisión invisible que
              te roba la vida poco a poco.
            </p>
          </FadeIn>

          <StaggerContainer className="space-y-3 mb-6" data-id="element-77">
            {['Cancelando planes en el último minuto porque el pecho te aprieta y sientes que no puedes respirar.', 'Despertándote a las 3 AM con taquicardia, sudando y pensando que esta vez sí te va a dar un infarto.', 'Sintiendo esa niebla mental constante que no te deja concentrarte en el trabajo ni disfrutar de tu familia.', 'Evitando lugares, personas o situaciones por miedo a que te dé un ataque en público y hagas el ridículo.', 'Agotado físicamente de estar en alerta 24/7, con los músculos en tensión constante.', "Sintiendo que te estás volviendo loco porque los médicos te dicen que 'no tienes nada, es solo estrés'.", 'Viendo cómo la vida pasa mientras tú solo intentas sobrevivir al día de hoy.'].map((bullet, i) => <StaggerItem key={i} data-id="element-78">
                <div className="flex items-start gap-4" data-id="element-79">
                  <X className="text-[#E07070] shrink-0 mt-1" size={20} data-id="element-80" />
                  <p className="text-lg text-cherub-text-secondary leading-relaxed" data-id="element-81">
                    {bullet}
                  </p>
                </div>
                {i < 6 && <div className="h-px w-full bg-white/5 mt-4" data-id="element-82" />}
              </StaggerItem>)}
          </StaggerContainer>

          <FadeIn className="text-center" data-id="element-83">
            <h3 className="text-xl md:text-2xl font-semibold text-gradient-gold" data-id="element-84">
              ¿Y si este fuera el último día que te sientes así?
            </h3>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 4: PRODUCT INTRO */}
      <section className="py-10 px-6 bg-cherub-bg-secondary bg-noise relative overflow-hidden" data-id="element-85">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,168,71,0.08)_0%,transparent_70%)] pointer-events-none" data-id="element-86" />

        <div className="max-w-4xl mx-auto relative-z text-center flex flex-col items-center" data-id="element-87">
          <FadeIn data-id="element-88">
            <div className="inline-block bg-cherub-gold-soft border border-[rgba(212,168,71,0.35)] text-cherub-gold-main font-bold text-xs tracking-[0.3em] uppercase py-2 px-6 rounded-full mb-3" data-id="element-89">
              CHERUB AGENCY™ MEGAPACK
            </div>

            <h2 className="font-serif font-bold text-4xl md:text-6xl text-gradient-gold mb-3 leading-tight pb-2" data-id="element-90">
              SISTEMA ZERO ANSIEDAD
            </h2>

            <p className="text-lg md:text-xl text-cherub-text-secondary mb-6" data-id="element-91">
              6 Tomos + 6 Bonos — 360+ páginas — Todo resuelto.
            </p>

            <GoldDivider className="mb-6" data-id="element-92" />

            <GoldButton onClick={scrollToPricing} className="text-lg py-5 px-12" data-id="element-93">
              → VER OPCIONES DE COMPRA
            </GoldButton>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 5: THE 6 TOMES */}
      <section className="py-8 px-6 bg-[#080C14] bg-noise" data-id="element-94">
        <div className="max-w-6xl mx-auto relative-z" data-id="element-95">
          <FadeIn className="text-center mb-6" data-id="element-96">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-97">
              EL SISTEMA COMPLETO
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-98">
              6 Tomos Completos
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-99">
            {[{
            num: '01',
            title: 'REGULACIÓN DEL SISTEMA NERVIOSO',
            desc: 'Por qué tu cuerpo entra en alarma de forma automática y cómo activar el freno biológico que siempre tuviste. Protocolo de choque incluido para crisis inmediatas.'
          }, {
            num: '02',
            title: 'LIBERACIÓN DE TENSIÓN SOMÁTICA',
            desc: 'Tu cuerpo guarda lo que la mente no procesó. Técnicas exactas para disolver años de tensión acumulada en psoas, diafragma y fascia.'
          }, {
            num: '03',
            title: 'REESTRUCTURACIÓN DE PENSAMIENTOS',
            desc: 'El cerebro ansioso catastrofiza de forma automática. Aquí lo reprogramas con neuroplasticidad real para que los pensamientos dejen de gobernarte.'
          }, {
            num: '04',
            title: 'CONEXIÓN Y PROPÓSITO VITAL',
            desc: 'La ansiedad te vacía de sentido con los años. Este tomo reconstruye la dirección, la identidad y el propósito que el miedo te fue robando.'
          }, {
            num: '05',
            title: 'PLAN DE SEGUIMIENTO DIARIO',
            desc: 'El conocimiento sin implementación no cambia nada. Este tomo convierte el sistema en un plan diario real que funciona incluso en los peores días.'
          }, {
            num: '06',
            title: 'MANTENIMIENTO Y PREVENCIÓN',
            desc: 'La transformación que no se mantiene se erosiona. Protocolos para que todo lo que construiste dure para siempre y no vuelvas a empezar de cero.'
          }].map((tomo, i) => <StaggerItem key={i} className="glass-card glass-card-hover rounded-2xl p-5 md:p-6 flex flex-col h-full" data-id="element-100">
                <div className="mb-3" data-id="element-101">
                  <div className="font-serif font-bold text-4xl text-gradient-gold opacity-90" data-id="element-102">
                    {tomo.num}
                  </div>
                </div>
                <h3 className="font-semibold text-xl text-cherub-text-main mb-2 uppercase tracking-wider" data-id="element-103">
                  {tomo.title}
                </h3>
                <p className="text-cherub-text-secondary leading-relaxed" data-id="element-104">
                  {tomo.desc}
                </p>
              </StaggerItem>)}
          </StaggerContainer>

          <FadeIn data-id="element-105">
            <div className="border border-cherub-gold-main/40 bg-[rgba(212,168,71,0.05)] rounded-2xl p-5 md:p-6 text-center max-w-4xl mx-auto" data-id="element-106">
              <p className="text-cherub-text-main text-xl md:text-2xl leading-relaxed font-sans" data-id="element-107">
                Cada tomo incluye: Protocolos listos para aplicar hoy —
                Explicación científica en lenguaje humano — Pasos exactos para
                cada situación — Fichas de seguimiento semanal — Adaptado a
                vidas reales e imperfectas
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 6: THE 6 BONUSES */}
      <section className="py-8 px-6 bg-[#0D1526] bg-noise" data-id="element-108">
        <div className="max-w-6xl mx-auto relative-z" data-id="element-109">
          <FadeIn className="text-center mb-6" data-id="element-110">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-111">
              BONOS INCLUIDOS
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-112">
              6 Bonos Estratégicos — Siempre Incluidos
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-113">
            {[{
            num: '1',
            title: 'KIT DE EMERGENCIA PARA ATAQUES DE ANSIEDAD',
            desc: 'Protocolo exacto para los primeros 60 segundos de un ataque de pánico. En casa, en la calle, en el trabajo y a las 3 de la madrugada.',
            value: '9,97€'
          }, {
            num: '2',
            title: 'GUÍA DE RUTINAS DE 5, 10 Y 20 MINUTOS',
            desc: 'Para cuando no tienes tiempo ni energía. Un menú de prácticas exactas para cada momento del día y cada tipo de jornada.',
            value: '7,97€'
          }, {
            num: '3',
            title: 'GUÍA QUÉ HACER EN UN DÍA MALO',
            desc: 'Qué priorizar, qué soltar sin culpa y cómo cerrar bien un día difícil para que mañana empiece desde un lugar diferente.',
            value: '7,97€'
          }, {
            num: '4',
            title: 'TRACKER DE PROGRESO ZERO ANSIEDAD',
            desc: 'El sistema que convierte tu transformación en evidencia visible. Para cuando crees que nada cambia y los números te dicen la verdad.',
            value: '9,97€'
          }, {
            num: '5',
            title: 'RITUALES DE RECONEXIÓN PROFUNDA',
            desc: 'Los rituales de mañana, noche y domingo que te devuelven a ti mismo cuando la ansiedad te ha alejado de quién realmente eres.',
            value: '9,97€'
          }, {
            num: '6',
            title: 'LOS 21 HÁBITOS DEL SISTEMA NERVIOSO FUERTE',
            desc: 'La arquitectura de una vida que ya no alimenta la ansiedad. No para gestionarla mejor. Para vivir una vida que ya no la necesite.',
            value: '9,97€'
          }].map((bono, i) => <StaggerItem key={i} className="glass-card glass-card-hover rounded-2xl p-5 md:p-6 relative" data-id="element-114">
                <div className="absolute top-0 left-0 bg-cherub-gold-main text-cherub-bg-main font-bold text-xs tracking-[0.2em] uppercase py-2 px-6 rounded-br-2xl rounded-tl-2xl" data-id="element-115">
                  BONO {bono.num}
                </div>
                <div className="mt-4" data-id="element-116">
                  <h3 className="font-semibold text-xl text-cherub-text-main mb-2 uppercase tracking-wider" data-id="element-117">
                    {bono.title}
                  </h3>
                  <p className="text-cherub-text-secondary mb-4 leading-relaxed" data-id="element-118">
                    {bono.desc}
                  </p>
                  <div className="h-px w-full bg-cherub-gold-soft mb-4" data-id="element-119" />
                  <div className="flex items-center gap-4" data-id="element-120">
                    <span className="text-[#8A8FA0] line-through text-xl" data-id="element-121">
                      {bono.value}
                    </span>
                    <span className="font-bold text-[#F0C040] text-xl" data-id="element-122">
                      → GRATIS
                    </span>
                  </div>
                </div>
              </StaggerItem>)}
          </StaggerContainer>

          <FadeIn data-id="element-123">
            <div className="bg-[rgba(212,168,71,0.08)] border border-[rgba(212,168,71,0.40)] rounded-2xl p-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 max-w-4xl mx-auto mb-6" data-id="element-124">
              <div className="text-cherub-text-main text-xl font-medium" data-id="element-125">
                Valor total bonos:
              </div>
              <div className="flex items-center gap-4" data-id="element-126">
                <span className="text-[#8A8FA0] line-through text-2xl" data-id="element-127">
                  55,82€
                </span>
                <span className="text-cherub-text-main text-2xl" data-id="element-128">→</span>
                <span className="font-bold text-[#F0C040] text-2xl" data-id="element-129">
                  GRATIS con tu MegaPack
                </span>
              </div>
            </div>

            <div className="flex justify-center" data-id="element-130">
              <GoldButton onClick={scrollToPricing} className="w-full md:w-auto text-lg py-5 px-12 animate-pulse-glow" data-id="element-131">
                → QUIERO MI MEGAPACK AHORA
              </GoldButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 7: URGENCY / TIMER */}
      <section className="py-8 px-6 bg-cherub-bg-main bg-noise border-y border-cherub-gold-soft relative" data-id="element-132">
        <div className="max-w-3xl mx-auto relative-z flex flex-col items-center text-center" data-id="element-133">
          <FadeIn className="flex flex-col items-center w-full" data-id="element-134">
            <Clock size={48} className="text-cherub-gold-main mb-3 drop-shadow-[0_0_30px_rgba(212,168,71,0.4)]" data-id="element-135" />
            <h2 className="font-bold text-sm md:text-base text-cherub-gold-main tracking-[0.3em] uppercase mb-6" data-id="element-136">
              PRECIO DE LANZAMIENTO — CIERRA EN:
            </h2>

            <div className="flex items-center justify-center gap-4 md:gap-6 mb-6" data-id="element-137">
              <div className="glass-card rounded-2xl p-4 md:p-5 min-w-[80px] md:min-w-[140px]" data-id="element-138">
                <div className="font-serif font-bold text-4xl md:text-6xl text-gradient-gold mb-1" data-id="element-139">
                  {String(timeLeft.hours).padStart(2, '0')}
                </div>
                <div className="text-xs text-cherub-text-tertiary uppercase tracking-widest" data-id="element-140">
                  HORAS
                </div>
              </div>
              <div className="text-2xl md:text-4xl text-cherub-gold-main font-serif mb-3" data-id="element-141">
                :
              </div>
              <div className="glass-card rounded-2xl p-4 md:p-5 min-w-[80px] md:min-w-[140px]" data-id="element-142">
                <div className="font-serif font-bold text-4xl md:text-6xl text-gradient-gold mb-1" data-id="element-143">
                  {String(timeLeft.minutes).padStart(2, '0')}
                </div>
                <div className="text-xs text-cherub-text-tertiary uppercase tracking-widest" data-id="element-144">
                  MINUTOS
                </div>
              </div>
              <div className="text-2xl md:text-4xl text-cherub-gold-main font-serif mb-3" data-id="element-145">
                :
              </div>
              <div className="glass-card rounded-2xl p-4 md:p-5 min-w-[80px] md:min-w-[140px]" data-id="element-146">
                <div className="font-serif font-bold text-4xl md:text-6xl text-gradient-gold mb-1" data-id="element-147">
                  {String(timeLeft.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs text-cherub-text-tertiary uppercase tracking-widest" data-id="element-148">
                  SEGUNDOS
                </div>
              </div>
            </div>

            <p className="text-lg text-cherub-text-secondary mb-4 flex items-center gap-2" data-id="element-149">
              <span className="text-2xl" data-id="element-150">⚠️</span> Después sube el precio. No
              vuelve.
            </p>

            <div className="w-full max-w-md bg-white/5 border border-cherub-gold-soft rounded-full h-2 overflow-hidden relative" data-id="element-151">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '72%'
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} className="absolute top-0 left-0 h-full bg-gold-gradient" data-id="element-152" />
            </div>
            <p className="text-xs text-cherub-text-tertiary mt-4" data-id="element-153">
              72% de plazas al precio de lanzamiento tomadas
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 8: PRICING */}
      <section id="pricing" className="py-8 px-6 bg-cherub-bg-secondary bg-noise" data-id="element-154">
        <div className="max-w-6xl mx-auto relative-z" data-id="element-155">
          <FadeIn className="text-center mb-6" data-id="element-156">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-157">
              ELIGE TU MEGAPACK
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-158">
              La inversión en tu tranquilidad
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch max-w-4xl mx-auto mb-6" data-id="element-159">
            {/* CARD 1 */}
            <FadeIn delay={0.1} className="h-full" data-id="element-160">
              <div onClick={() => setSelectedPack('pack1')} className={`cursor-pointer transition-all duration-300 rounded-[32px] p-5 md:p-6 flex flex-col h-full border-2 ${selectedPack === 'pack1' ? 'bg-[rgba(212,168,71,0.08)] border-cherub-gold-main shadow-gold-glow' : 'glass-card border-transparent opacity-80 hover:opacity-100'}`} data-id="element-161">
                <div className="flex items-center gap-4 mb-3" data-id="element-162">
                  <Book size={40} className="text-cherub-gold-main" data-id="element-163" />
                  <div data-id="element-164">
                    <h3 className="font-bold text-xs text-cherub-gold-main uppercase tracking-[0.2em]" data-id="element-165">
                      MEGAPACK 1
                    </h3>
                    <h4 className="font-serif text-xl text-cherub-text-main" data-id="element-166">
                      SISTEMA ZERO
                    </h4>
                  </div>
                </div>

                <p className="text-cherub-text-secondary text-base mb-3" data-id="element-167">
                  6 Tomos + 6 Bonos
                </p>

                <ul className="space-y-2 mb-4 text-base" data-id="element-168">
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-169">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-170" />
                    Los 6 Tomos en PDF
                  </li>
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-171">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-172" />
                    Los 6 Bonos Estratégicos
                  </li>
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-173">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-174" />
                    Acceso de por vida
                  </li>
                </ul>

                <div className="h-px w-full bg-cherub-gold-soft mb-3" data-id="element-175" />

                <div className="mb-4" data-id="element-176">
                  <div className="text-cherub-text-tertiary line-through text-base mb-1" data-id="element-177">
                    75,76€
                  </div>
                  <div className="font-bold text-4xl text-gradient-gold" data-id="element-178">
                    14,97€
                  </div>
                </div>

                <SecondaryButton onClick={e => {
                e.stopPropagation();
                window.open('https://checkout.cherubagency.com/cart/56697983304004:1', '_blank');
              }} className={`w-full py-4 text-base ${selectedPack === 'pack1' ? 'bg-cherub-gold-main text-cherub-bg-main' : ''}`} data-id="element-179">
                  QUIERO MI MEGAPACK
                </SecondaryButton>
              </div>
            </FadeIn>

            {/* CARD 2 - HIGHLIGHTED */}
            <FadeIn delay={0.2} className="h-full" data-id="element-180">
              <div onClick={() => setSelectedPack('pack2')} className={`cursor-pointer transition-all duration-300 rounded-[32px] p-5 md:p-6 flex flex-col h-full relative border-2 ${selectedPack === 'pack2' ? 'bg-[rgba(212,168,71,0.1)] border-cherub-gold-bright shadow-gold-glow scale-[1.02]' : 'glass-card border-transparent opacity-80 hover:opacity-100'}`} data-id="element-181">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold-gradient text-[#080C14] font-bold text-xs tracking-[0.2em] uppercase py-2 px-8 rounded-full whitespace-nowrap z-20" data-id="element-182">
                  ⭐ MÁS ELEGIDO
                </div>

                <div className="flex items-center gap-4 mb-3" data-id="element-183">
                  <Trophy size={48} className="text-cherub-gold-bright drop-shadow-[0_0_20px_rgba(240,192,64,0.4)]" data-id="element-184" />
                  <div data-id="element-185">
                    <h3 className="font-bold text-xs text-gradient-gold uppercase tracking-[0.2em]" data-id="element-186">
                      MEGAPACK COMPLETO
                    </h3>
                    <h4 className="font-serif text-xl text-cherub-text-main" data-id="element-187">
                      SISTEMA + EXTRAS VIP
                    </h4>
                  </div>
                </div>

                <p className="text-cherub-text-secondary text-base mb-3" data-id="element-188">
                  La solución definitiva y más rápida
                </p>

                <ul className="space-y-2 mb-4 text-base" data-id="element-189">
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-190">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-191" />
                    <strong data-id="element-192">Todo lo del MegaPack 1</strong>
                  </li>
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-193">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-194" />
                    <strong data-id="element-195">Protocolo Biológico Avanzado</strong>
                  </li>
                  <li className="flex items-start gap-3 text-cherub-text-main" data-id="element-196">
                    <Check size={20} className="text-cherub-gold-main shrink-0 mt-1" data-id="element-197" />
                    Guía alimentación anti-inflamatoria
                  </li>
                </ul>

                <div className="h-px w-full bg-cherub-gold-soft mb-3" data-id="element-198" />

                <div className="mb-4" data-id="element-199">
                  <div className="text-cherub-text-tertiary line-through text-base mb-1" data-id="element-200">
                    157,49€
                  </div>
                  <div className="font-bold text-4xl text-gradient-gold" data-id="element-201">
                    23,97€
                  </div>
                </div>

                <GoldButton onClick={e => {
                e.stopPropagation();
                window.open('https://checkout.cherubagency.com/cart/56697984024900:1', '_blank');
              }} className="w-full py-4 text-base animate-pulse-glow" data-id="element-202">
                  QUIERO EL SISTEMA COMPLETO
                </GoldButton>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} data-id="element-203">
            <div className="flex flex-wrap justify-center gap-8 md:gap-12 text-sm text-cherub-text-tertiary" data-id="element-204">
              <div className="flex items-center gap-2" data-id="element-205">
                <Lock size={20} data-id="element-206" /> Pago 100% seguro
              </div>
              <div className="flex items-center gap-2" data-id="element-207">
                <Zap size={20} data-id="element-208" /> Acceso en 5 minutos
              </div>
              <div className="flex items-center gap-2" data-id="element-209">
                <Shield size={20} data-id="element-210" /> Garantía 7 días
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 9: COMPARISON TABLE */}
      <section className="py-8 px-6 bg-cherub-bg-main bg-noise" data-id="element-211">
        <div className="max-w-4xl mx-auto relative-z" data-id="element-212">
          <FadeIn className="text-center mb-6" data-id="element-213">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-214">
              COMPARATIVA COMPLETA
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-215">
              ¿Qué añade el MegaPack Completo?
            </h2>
          </FadeIn>

          <FadeIn data-id="element-216">
            <div className="overflow-x-auto pb-4 mb-6" data-id="element-217">
              <div className="min-w-[800px] rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.05)]" data-id="element-218">
                {/* Header */}
                <div className="grid grid-cols-3 bg-[rgba(212,168,71,0.15)]" data-id="element-219">
                  <div className="p-4 font-bold text-cherub-gold-main text-lg" data-id="element-220">
                    Situación
                  </div>
                  <div className="p-4 font-bold text-cherub-text-secondary text-center text-lg" data-id="element-221">
                    Solo MegaPack 1
                  </div>
                  <div className="p-4 font-bold text-cherub-gold-bright text-center bg-[rgba(212,168,71,0.05)] border-x border-[rgba(212,168,71,0.20)] text-lg" data-id="element-222">
                    MegaPack Completo
                  </div>
                </div>

                {/* Rows */}
                {[{
                label: 'Tu ansiedad tiene base inflamatoria biológica',
                m1: 'No tienes herramientas específicas',
                mc: 'Protocolo completo de micro-nutrición antiinflamatoria cerebral'
              }, {
                label: 'Insomnio crónico y despertares a las 3 AM',
                m1: 'Tienes las técnicas de regulación',
                mc: 'Stack exacto de suplementación nocturna para el sueño profundo'
              }, {
                label: 'Ansiedad social antes de eventos importantes',
                m1: 'Tienes las bases de regulación',
                mc: 'Protocolo pre-evento con dosis y horarios exactos'
              }, {
                label: 'Agotamiento crónico con ansiedad simultánea',
                m1: 'Tienes el trabajo somático y cognitivo',
                mc: 'Protocolo completo de restauración adrenal y energía sin nervios'
              }, {
                label: 'Déficits nutricionales que amplifican síntomas',
                m1: 'No está cubierto en profundidad',
                mc: 'Guía completa de los 21 nutrientes clave para el sistema nervioso'
              }, {
                label: 'Quieres que la transformación sea permanente',
                m1: 'Tienes la base mental y emocional',
                mc: 'Hoja de ruta biológica para blindar el sistema nervioso para siempre'
              }].map((row, i) => <div key={i} className={`grid grid-cols-3 border-t border-[rgba(255,255,255,0.05)] ${i % 2 === 0 ? 'bg-[rgba(255,255,255,0.02)]' : 'bg-transparent'}`} data-id="element-223">
                    <div className="p-4 text-cherub-text-secondary flex items-center text-sm md:text-base" data-id="element-224">
                      {row.label}
                    </div>
                    <div className="p-4 flex items-center justify-center text-center text-sm md:text-base text-cherub-text-tertiary" data-id="element-225">
                      {row.m1}
                    </div>
                    <div className="p-4 flex items-center justify-center text-center text-sm md:text-base bg-[rgba(212,168,71,0.05)] border-x border-[rgba(212,168,71,0.20)] text-cherub-gold-bright font-medium" data-id="element-226">
                      {row.mc}
                    </div>
                  </div>)}
              </div>
            </div>
          </FadeIn>

          <FadeIn data-id="element-227">
            <div className="border border-cherub-gold-main/40 rounded-2xl p-5 md:p-8 bg-[rgba(212,168,71,0.03)]" data-id="element-228">
              <div className="flex items-center gap-3 mb-2" data-id="element-229">
                <CheckCircle2 className="text-cherub-gold-main" size={24} data-id="element-230" />
                <h3 className="text-xl font-bold text-cherub-text-main" data-id="element-231">
                  El MegaPack Completo incluye además:
                </h3>
              </div>
              <h4 className="text-cherub-gold-main font-bold text-sm mb-2 uppercase tracking-[0.2em]" data-id="element-232">
                PROTOCOLO DE MICRO-NUTRICIÓN Y SUPLEMENTACIÓN AVANZADA
              </h4>
              <p className="text-cherub-text-main mb-4 text-lg" data-id="element-233">
                60 páginas de bioquímica aplicada a la ansiedad.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3" data-id="element-234">
                {['Bloque 1: La biología real de la ansiedad — inflamación, neurotransmisores y eje intestino-cerebro', 'Bloque 2: El Combo Base Cherub — Los 3 suplementos no negociables con dosis exactas y horarios específicos', 'Bloque 3: Adaptógenos y Fitoterapia Clínica — Ashwagandha, Rhodiola, L-Teanina y hongos funcionales', 'Bloque 4: Protocolos de Intervención — Sueño Profundo, Rescate Social y Energía sin Nervios', 'Bloque 5: La Dieta de Reinicio Biológico — Lista de la compra Cherub y protocolo antiinflamatorio', 'Bloque 6: Horario Maestro completo de suplementación'].map((item, i) => <li key={i} className="flex items-start gap-3 text-cherub-text-secondary text-base leading-relaxed" data-id="element-235">
                    <span className="text-cherub-gold-main mt-1" data-id="element-236">→</span>
                    {item}
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 10: TESTIMONIALS 2 */}
      <section className="py-8 px-6 bg-cherub-bg-secondary bg-noise" data-id="element-237">
        <div className="max-w-6xl mx-auto relative-z" data-id="element-238">
          <FadeIn className="text-center mb-6" data-id="element-239">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-240">
              MÁS DE 500 PERSONAS TRANSFORMADAS
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-241">
              Más personas que ya lo tienen
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-242">
            {[{
            name: 'Miguel A.',
            city: 'Sevilla',
            pack: 'MEGAPACK COMPLETO',
            text: 'Los audios nocturnos del pack completo son magia. Llevaba años despertándome con taquicardia y ahora duermo del tirón. Vale cada céntimo.'
          }, {
            name: 'Sofía T.',
            city: 'Bilbao',
            pack: 'MEGAPACK 1',
            text: 'El tomo 4 sobre pensamientos intrusivos me voló la cabeza. Por fin entiendo por qué mi mente me saboteaba y cómo pararlo en seco.'
          }, {
            name: 'Javier P.',
            city: 'Málaga',
            pack: 'MEGAPACK COMPLETO',
            text: 'El protocolo biológico me hizo entender que mi ansiedad también venía del estómago. Cambié 3 cosas de mi dieta y la ansiedad base bajó un 80%.'
          }, {
            name: 'Carmen L.',
            city: 'Zaragoza',
            pack: 'MEGAPACK 1',
            text: "Práctico, directo y sin paja. No es el típico libro de autoayuda que te dice 'respira y piensa en positivo'. Es ciencia aplicada."
          }, {
            name: 'David M.',
            city: 'Alicante',
            pack: 'MEGAPACK COMPLETO',
            text: 'Compré el completo por los audios y la guía de alimentación. Ha sido la mejor inversión en salud que he hecho en mis 34 años.'
          }, {
            name: 'Ana R.',
            city: 'Granada',
            pack: 'MEGAPACK 1',
            text: 'El bono de rescate inmediato lo llevo siempre en el móvil. Solo saber que lo tengo ahí me da una paz mental increíble.'
          }].map((t, i) => <StaggerItem key={i} className="glass-card glass-card-hover rounded-2xl p-5 md:p-6 flex flex-col h-full relative overflow-hidden" data-id="element-243">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-cherub-gold-main opacity-50" data-id="element-244" />
                <p className="font-quote italic text-xl text-cherub-text-main mb-4 flex-grow" data-id="element-245">
                  "{t.text}"
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-auto" data-id="element-246">
                  <div className="flex items-center gap-4" data-id="element-247">
                    <div className="w-12 h-12 rounded-full border border-cherub-gold-soft bg-cherub-bg-tertiary flex items-center justify-center text-cherub-gold-main font-serif font-bold text-lg" data-id="element-248">
                      {t.name[0]}
                    </div>
                    <div data-id="element-249">
                      <div className="font-semibold text-cherub-text-main" data-id="element-250">
                        {t.name}
                      </div>
                      <div className="text-sm text-cherub-gold-muted" data-id="element-251">
                        {t.city}
                      </div>
                    </div>
                  </div>
                  <div className="bg-[rgba(212,168,71,0.15)] text-cherub-gold-main font-bold text-xs tracking-widest uppercase py-2 px-6 rounded-full self-start sm:self-auto" data-id="element-252">
                    {t.pack}
                  </div>
                </div>
              </StaggerItem>)}
          </StaggerContainer>
        </div>
      </section>

      {/* SECTION 11: GUARANTEE */}
      <section className="py-10 px-6 bg-cherub-bg-main bg-noise border-t border-cherub-gold-soft relative overflow-hidden" data-id="element-253">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(212,168,71,0.10)_0%,transparent_50%)] pointer-events-none" data-id="element-254" />

        <div className="max-w-4xl mx-auto relative-z text-center flex flex-col items-center" data-id="element-255">
          <FadeIn className="flex flex-col items-center w-full" data-id="element-256">
            <Shield size={64} className="text-cherub-gold-main mb-4 drop-shadow-[0_0_50px_rgba(212,168,71,0.5)]" strokeWidth={1} data-id="element-257" />
            <GoldDivider className="mb-4" data-id="element-258" />

            <h2 className="font-serif font-bold text-3xl md:text-5xl text-gradient-gold mb-4" data-id="element-259">
              Garantía 7 Días — Sin Preguntas
            </h2>

            <p className="text-lg md:text-xl text-cherub-text-secondary max-w-2xl mb-6 leading-relaxed" data-id="element-260">
              Estamos tan seguros de que este sistema funciona que asumimos todo
              el riesgo. Tienes 7 días completos para leer los tomos, escuchar
              los audios y aplicar los protocolos. Si sientes que no es para ti,
              nos mandas un email y te devolvemos el 100% de tu dinero. Sin
              letra pequeña.
            </p>

            <div className="glass-card rounded-2xl p-5 border-[rgba(212,168,71,0.3)] mb-6 inline-block" data-id="element-261">
              <p className="text-cherub-text-secondary mb-1 text-lg" data-id="element-262">
                Tasa de devoluciones actual:
              </p>
              <p className="font-serif font-bold text-5xl text-cherub-gold-main" data-id="element-263">
                Menos del 1%
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-cherub-text-tertiary" data-id="element-264">
              <Lock size={16} data-id="element-265" /> Pago encriptado SSL de 256 bits
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SECTION 12: FAQ */}
      <section className="py-8 px-6 bg-cherub-bg-secondary bg-noise" data-id="element-266">
        <div className="max-w-4xl mx-auto relative-z" data-id="element-267">
          <FadeIn className="text-center mb-6" data-id="element-268">
            <p className="text-cherub-gold-main font-bold text-sm tracking-[0.2em] uppercase mb-2" data-id="element-269">
              PREGUNTAS FRECUENTES
            </p>
            <h2 className="font-serif font-bold text-3xl md:text-5xl text-cherub-text-main" data-id="element-270">
              Lo que más nos preguntan
            </h2>
          </FadeIn>

          <div className="space-y-3 mb-6" data-id="element-271">
            {[{
            q: '¿Es un producto físico o digital?',
            a: 'Es 100% digital. Recibirás acceso inmediato a todos los PDFs y audios (si eliges el pack completo) en tu correo electrónico apenas 5 minutos después de la compra. Puedes leerlo en tu móvil, tablet o PC, o imprimirlo si lo prefieres.'
          }, {
            q: '¿Sirve si llevo años con ansiedad y he probado de todo?',
            a: "Sí. De hecho, está diseñado específicamente para casos crónicos. No vas a encontrar los típicos consejos de 'respira hondo'. Aquí atacamos la biología del miedo y reprogramamos el sistema nervioso desde la raíz."
          }, {
            q: '¿Cuánto tardaré en ver resultados?',
            a: "El 'Protocolo de Choque' (Tomo 2) te dará herramientas para detener ataques en minutos desde el primer día. La reducción de la ansiedad general (ansiedad base) suele notarse significativamente entre la semana 2 y 3 aplicando el sistema."
          }, {
            q: '¿Es seguro el pago?',
            a: 'Totalmente. Utilizamos pasarelas de pago internacionales con encriptación bancaria SSL. Nosotros nunca tenemos acceso a los datos de tu tarjeta.'
          }, {
            q: '¿Cómo funciona la garantía?',
            a: 'Si dentro de los primeros 7 días sientes que el material no cumple tus expectativas, solo tienes que enviar un email a soporte@cherubagency.com y procesaremos la devolución de tu dinero en 24 horas. Sin hacerte preguntas incómodas.'
          }].map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-272">
                <div className={`rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === i ? 'bg-[rgba(212,168,71,0.05)] border border-[rgba(212,168,71,0.35)]' : 'bg-[rgba(255,255,255,0.03)] border border-[rgba(212,168,71,0.15)]'}`} data-id="element-273">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 md:p-6 text-left" data-id="element-274">
                    <span className="font-semibold text-lg md:text-xl text-cherub-text-main pr-8" data-id="element-275">
                      {faq.q}
                    </span>
                    {openFaq === i ? <X size={24} className="text-cherub-gold-main shrink-0" data-id="element-276" /> : <ChevronDown size={24} className="text-cherub-gold-main shrink-0" data-id="element-277" />}
                  </button>
                  <div className={`px-5 md:px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-278">
                    <p className="text-lg text-cherub-text-secondary leading-relaxed" data-id="element-279">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.6} data-id="element-280">
            <div className="py-12" data-id="element-281">
              <GoldDivider className="w-full max-w-md opacity-50" data-id="element-282" />
            </div>
          </FadeIn>

          {/* Final CTA Mini Cards */}
          <FadeIn data-id="element-283">
            <div className="max-w-3xl mx-auto" data-id="element-284">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-id="element-285">
                {/* Option 1 */}
                <div onClick={() => setSelectedPack('pack1')} className={`cursor-pointer transition-all duration-300 rounded-2xl p-5 flex items-center justify-between border-2 ${selectedPack === 'pack1' ? 'bg-[rgba(212,168,71,0.12)] border-cherub-gold-main shadow-gold-glow' : 'glass-card border-white/5 opacity-60 hover:opacity-100'}`} data-id="element-286">
                  <div className="flex flex-col items-start" data-id="element-287">
                    <span className="text-xs font-bold text-cherub-gold-main uppercase tracking-widest mb-1" data-id="element-288">
                      OPCIÓN 1
                    </span>
                    <span className="font-bold text-xl text-cherub-text-main" data-id="element-289">
                      MEGAPACK ZERO
                    </span>
                    <span className="text-cherub-gold-bright font-bold text-lg" data-id="element-290">
                      14,97€
                    </span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedPack === 'pack1' ? 'border-cherub-gold-main bg-cherub-gold-main scale-110' : 'border-white/20'}`} data-id="element-291">
                    {selectedPack === 'pack1' && <Check size={14} className="text-cherub-bg-main" data-id="element-292" />}
                  </div>
                </div>

                {/* Option 2 */}
                <div onClick={() => setSelectedPack('pack2')} className={`cursor-pointer transition-all duration-300 rounded-2xl p-5 flex items-center justify-between border-2 ${selectedPack === 'pack2' ? 'bg-[rgba(212,168,71,0.15)] border-cherub-gold-bright shadow-gold-glow scale-[1.02]' : 'glass-card border-white/5 opacity-60 hover:opacity-100'}`} data-id="element-293">
                  <div className="flex flex-col items-start" data-id="element-294">
                    <span className="text-xs font-bold text-cherub-gold-bright uppercase tracking-widest mb-1" data-id="element-295">
                      ⭐ MÁS ELEGIDO
                    </span>
                    <span className="font-bold text-xl text-cherub-text-main" data-id="element-296">
                      SISTEMA COMPLETO
                    </span>
                    <span className="text-cherub-gold-bright font-bold text-lg" data-id="element-297">
                      23,97€
                    </span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedPack === 'pack2' ? 'border-cherub-gold-bright bg-cherub-gold-bright scale-110' : 'border-white/20'}`} data-id="element-298">
                    {selectedPack === 'pack2' && <Check size={14} className="text-cherub-bg-main" data-id="element-299" />}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center" data-id="element-300">
                <GoldButton onClick={() => window.open(selectedPack === 'pack1' ? 'https://checkout.cherubagency.com/cart/56697983304004:1' : 'https://checkout.cherubagency.com/cart/56697984024900:1', '_blank')} className="w-full py-6 text-xl shadow-[0_0_60px_rgba(212,168,71,0.4)]" data-id="element-301">
                  {selectedPack === 'pack1' ? 'QUIERO MI MEGAPACK' : 'QUIERO EL SISTEMA COMPLETO'}
                </GoldButton>
                <p className="mt-4 text-sm text-cherub-text-tertiary flex items-center gap-2" data-id="element-302">
                  <Lock size={16} data-id="element-303" /> Pago 100% seguro y encriptado
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050810] border-t border-[rgba(212,168,71,0.20)] py-8 px-6" data-id="element-304">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center" data-id="element-305">
          <h2 className="font-serif text-cherub-text-main tracking-wide mb-2 text-[20px]" data-id="element-306">
            CHERUB AGENCY
            <span className="text-cherub-gold-main text-lg align-top" data-id="element-307">™</span>
          </h2>
          <p className="text-cherub-text-tertiary mb-6 text-[16px]" data-id="element-308">
            Salud mental premium para el mundo real.
          </p>

          <GoldDivider className="mb-6 w-[300px]" data-id="element-309" />

          <div className="flex flex-col md:flex-row gap-6 md:gap-12 mb-8" data-id="element-310">
            <a href="mailto:soporte@cherubagency.com" className="text-cherub-text-secondary hover:text-cherub-gold-main transition-colors text-[14px]" data-id="element-311">
              soporte@cherubagency.com
            </a>
            <a href="#" className="text-cherub-text-secondary hover:text-cherub-gold-main transition-colors text-[14px]" data-id="element-312">
              @cherubagency
            </a>
            <a href="#" className="text-cherub-text-secondary hover:text-cherub-gold-main transition-colors text-[14px]" data-id="element-313">
              www.cherubagency.com
            </a>
          </div>

          <div className="w-full h-px bg-[rgba(255,255,255,0.05)] mb-6" data-id="element-314" />

          <div className="flex flex-col md:flex-row justify-between items-center w-full gap-6" data-id="element-315">
            <p className="text-sm text-cherub-text-tertiary" data-id="element-316">
              © Cherub Agency™ 2026 — Todos los derechos reservados.
            </p>
            <div className="flex gap-6 text-sm text-cherub-text-tertiary" data-id="element-317">
              <a href="#" className="hover:text-cherub-gold-main transition-colors" data-id="element-318">
                Aviso legal
              </a>
              <span data-id="element-319">|</span>
              <a href="#" className="hover:text-cherub-gold-main transition-colors" data-id="element-320">
                Política de privacidad
              </a>
              <span data-id="element-321">|</span>
              <a href="#" className="hover:text-cherub-gold-main transition-colors" data-id="element-322">
                Política de cookies
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}